// Reducer for a "Dictionary" (an object which has keys and values all of the same type).
//
// Updating will do an object merge on the values so the values must be an object if you want to use the update action.
// We can implement support for other types of values later.
//
// Example object structure:
//
// const error = {
//     '7289f92-5a92-4938-87d3-44ac6dad91b6':
//     {
//         isVisible: true,
//         message: "An error occurred"
//     }
// }
//
// Declaration:
// const [errors, dispatchErrors] = useReducer(ObjectReducer, {});
//
// Add a value:
// dispatchErrors({ type: 'add', key: id, value: { message, isVisible: true } });
//
// Remove a value:
// dispatchErrors({type: 'remove', key: id});
//
// Update a value:
// dispatchErrors({ type: 'update', key: id, value: { isVisible: false } });
// First, define the structure of the individual items in the dictionary state

interface DictionaryItem {
  isVisible: boolean
  message: string
}

// Define the state as a Record where each key is a string and the value is of type DictionaryItem
interface DictionaryState {
  [key: string]: DictionaryItem
}

// Define the action types as a union of possible actions
type DictionaryAction =
  | { type: 'add'; key: string; value: DictionaryItem } // Action adds a full DictionaryItem
  | { type: 'remove'; key: string } // Action removes an item by key
  | { type: 'update'; key: string; value: Partial<DictionaryItem> } // Action updates an item, possibly partially

// The reducer function itself with typed parameters and return type
export default function DictionaryReducer(
  state: DictionaryState,
  action: DictionaryAction
): DictionaryState {
  switch (action.type) {
    case 'add':
      // For 'add', simply set the value
      return { ...state, [action.key]: action.value }
    case 'update': {
      // For 'update', merge updates into existing item if item exists
      const existingItem = state[action.key]
      if (!existingItem) {
        // Item not found, no update to apply
        return state
      }
      return {
        ...state,
        [action.key]: { ...existingItem, ...action.value },
      }
    }
    case 'remove': {
      // Deconstruct to remove the item
      const { [action.key]: removed, ...newState } = state
      return newState
    }
    default:
      throw new Error(`Unhandled action type`)
  }
}
